import { fromMask, PLATFORMS, type PlatformKey } from '@dayone/core/dictionaries';
import { GameStatus, parseReleaseDate } from '@dayone/core/release-date';
import { type CSSProperties, memo } from 'react';
import type { CatalogItem } from '../../filters/catalog.ts';
import { daysUntil } from '../../filters/timeline.ts';
import { t } from '../../i18n/zh-TW.ts';
import { countdown, formatRelease } from '../../lib/format.ts';
import { Cover } from '../../ui/Cover.tsx';
import { Hint } from '../../ui/Hint.tsx';
import { ICON, TriangleAlert } from '../../ui/icons/index.ts';
import { PlatformList } from '../../ui/PlatformList.tsx';
import { CalendarBadge } from '../gcal/CalendarBadge.tsx';
import { EarlyEditionChip, EditionsBadge } from './editions.tsx';

function PlatformDates({ item, currentYear }: { item: CatalogItem; currentYear: number }) {
  const pd = item.row.pd;
  if (!pd?.length) return null;
  const lines = pd.map(([idx, d]) => {
    const key = PLATFORMS[idx]?.key as PlatformKey | undefined;
    const parsed = parseReleaseDate(d);
    return `${key ? t.platforms[key] : '?'} ${parsed ? formatRelease(parsed, currentYear) : d}`;
  });
  return (
    <Hint content={t.card.platformDatesHint(lines)}>
      <button type="button" className="badge badge-info">
        {t.card.platformDates}
      </button>
    </Hint>
  );
}

interface GameCardProps {
  item: CatalogItem;
  currentYear: number;
  /** 使用者時區的今天：7 天內發售的遊戲顯示倒數 */
  today: number;
  onOpen: (id: string) => void;
}

export const GameCard = memo(function GameCard({ item, currentYear, today, onOpen }: GameCardProps) {
  const { row, date } = item;
  const days = daysUntil(item, today);
  const soon = days !== null && days >= 0 && days <= 6 && (!item.released || item.releaseDay) ? countdown(days) : null;
  const platforms = fromMask(PLATFORMS, row.p) as PlatformKey[];
  const titleId = `title-${row.id}`;
  return (
    <article
      className="card card-clickable"
      aria-labelledby={titleId}
      data-game={row.id}
      style={{ '--vt-name': `card-${row.id}` } as CSSProperties}
    >
      <Cover id={row.id} name={row.n} cover={row.c} />
      <div className="card-body">
        <h3 id={titleId} className="card-title">
          {/* 整張卡片可點（延伸的點擊區域），標題本身是按鈕，鍵盤可聚焦 */}
          <button type="button" className="card-link" onClick={() => onOpen(row.id)} aria-label={t.detail.open(row.n)}>
            {row.n}
          </button>
        </h3>
        {row.z && <p className="card-subtitle">{row.z}</p>}
        <div className="card-meta">
          <time className="card-date num" dateTime={row.d}>
            {formatRelease(date, currentYear)}
          </time>
          {date.p !== 'day' && (
            <Hint content={t.date.precisionHint[date.p]}>
              <button type="button" className="badge badge-muted">
                {t.date.precision[date.p]}
              </button>
            </Hint>
          )}
          {soon && (
            <span className={days === 0 ? 'badge badge-soon badge-today' : 'badge badge-soon'}>
              {days === 0 ? soon.unit : `${soon.value} ${soon.unit}`}
            </span>
          )}
          {!item.released && row.st === GameStatus.earlyAccess && (
            <Hint content={t.card.earlyAccessUpcomingHint}>
              <button type="button" className="badge badge-accent">
                {t.card.earlyAccessNow}
              </button>
            </Hint>
          )}
          {item.released && !item.releaseDay && row.st === GameStatus.earlyAccess && (
            <span className="badge badge-accent">{t.card.earlyAccess}</span>
          )}
          {item.released && !item.releaseDay && row.st !== GameStatus.earlyAccess && (
            <span className="badge badge-ok">{t.card.released}</span>
          )}
          {item.stale && (
            <Hint content={t.card.staleHint}>
              <button type="button" className="badge badge-warn">
                <TriangleAlert {...ICON} />
                {t.card.stale}
              </button>
            </Hint>
          )}
          {row.cf === 1 && (
            <Hint content={t.card.conflictHint}>
              <button type="button" className="badge badge-warn">
                <TriangleAlert {...ICON} />
                {t.card.conflict}
              </button>
            </Hint>
          )}
          <PlatformDates item={item} currentYear={currentYear} />
          <EditionsBadge row={row} currentYear={currentYear} />
          <EarlyEditionChip item={item} />
          <CalendarBadge row={row} currentYear={currentYear} />
        </div>
        <PlatformList platforms={platforms} />
        {!item.released && item.hypePct !== undefined && (
          <Hint content={t.card.hypeHint(item.hypePct, row.hy ?? 0)}>
            <button type="button" className="hype" aria-label={`${t.card.hype} ${item.hypePct}`}>
              <span className="hype-label">{t.card.hype}</span>
              <span className="hype-bar" aria-hidden="true">
                <span className="hype-fill" style={{ width: `${Math.max(4, item.hypePct)}%` }} />
              </span>
              <span className="hype-value num">{item.hypePct}</span>
            </button>
          </Hint>
        )}
      </div>
    </article>
  );
});
